import React from 'react';

/**
 * Iter-52.2 — AI-disclaimer banner. PERMANENT, non-dismissable, sticky
 * at the BOTTOM of the viewport, and DELIBERATELY low-key visually so
 * it doesn't grab attention on page-load.
 *
 * Styling philosophy: mirror how Claude/ChatGPT display their model
 * disclaimers — small font, muted color, no icon/border/shadow.
 * A parent who looks for it will see it; a child scanning the page
 * for the "read aloud" button won't have their eye pulled to it.
 *
 * Placement rules:
 *  • Fixed, spanning the bottom of the viewport, sitting directly
 *    above the BottomNav (which is `fixed bottom-0 z-40`). z-30 so
 *    the nav stays on top and remains tappable.
 *  • The disclaimer text is centered on a lightly-tinted backdrop
 *    with a soft top-hairline separator to distinguish it from the
 *    reading content, but WITHOUT visual weight (no bold, no
 *    saturated color, no icon).
 *
 * Callers must ensure their scrollable content leaves enough bottom
 * padding (≥ pb-40 / 160px) so the last line of content is not
 * obscured by the banner + nav combined.
 */
export default function AiDisclaimerBanner({ language = 'en', testid = 'ai-disclaimer-banner' }) {
  const text = language === 'hi'
    ? 'ReadBuddy एक AI है और गलती कर सकता है। कृपया अपने बच्चे की पढ़ाई पर बीच-बीच में नज़र रखें।'
    : "ReadBuddy is AI and can make mistakes. Please check your child's reading time to time.";

  return (
    <div
      data-testid={testid}
      role="note"
      className="fixed left-0 right-0 z-30 pointer-events-none"
      // Sit just above the BottomNav (nav is ~78px tall including
      // safe-area). Using 78px offset places the banner flush against
      // the nav's top edge — the banner itself has a subtle opaque
      // backdrop matching the app background so scrolling content
      // that would otherwise pass behind it stays readable up until
      // it hits the disclaimer, then cleanly disappears.
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 78px)' }}
    >
      <div className="pointer-events-auto bg-[#EAF4FB]/95 backdrop-blur-[2px] py-1.5 px-6">
        <p
          className={`text-center max-w-6xl mx-auto text-[11px] md:text-xs font-normal text-slate-500 leading-snug ${language === 'hi' ? 'font-hi' : ''}`}
        >
          {text}
        </p>
      </div>
    </div>
  );
}
